import { motion } from "framer-motion";
import { splitScientific } from "../lib/describe";
import { toSuperscript } from "../lib/format";
import { cn } from "../lib/cn";

/**
 * The exponent-forward hero readout. People grasp powers of ten, so the "10ᴱ"
 * is the star: gold and pulsing whenever the exponent changes. The mantissa
 * ("1.3 ×") rides smaller in front.
 *
 * - `compact`  : one-line version for the pinned mobile ticker.
 * - `size="md"`: the persistent desktop side panel.
 * - `size="xl"`: the full-drama finale (default).
 */
export default function HeroNumber({
  value,
  compact = false,
  size = "xl",
}: {
  value: number;
  compact?: boolean;
  size?: "md" | "xl";
}) {
  const positive = isFinite(value) && value > 0;
  const { mantissa, exponent } = splitScientific(value);
  // For human-readable exponents (10^-2 .. 10^3) a plain decimal reads better.
  const plainRange = positive && exponent >= -2 && exponent <= 3;

  if (compact) {
    return (
      <span className="font-mono tabular-nums text-gold">
        {!positive ? (
          "≈ 0"
        ) : plainRange ? (
          formatPlain(value)
        ) : (
          <>
            {mantissa.toFixed(1)} × 10
            <sup className="text-[0.7em]">{toSuperscript(exponent)}</sup>
          </>
        )}
      </span>
    );
  }

  const s =
    size === "md"
      ? {
          approx: "text-lg",
          big: "text-5xl sm:text-6xl",
          mantissa: "text-xl sm:text-2xl mb-1.5",
          base: "text-4xl sm:text-5xl",
          exp: "text-3xl sm:text-4xl",
        }
      : {
          approx: "text-2xl",
          big: "text-7xl sm:text-8xl",
          mantissa: "text-2xl sm:text-3xl mb-2 sm:mb-3",
          base: "text-6xl sm:text-8xl",
          exp: "text-5xl sm:text-7xl",
        };

  const glow = "drop-shadow-[0_0_30px_rgba(255,206,110,0.4)]";

  return (
    <div className="flex flex-col items-center">
      <span
        className={cn(
          "mb-2 font-display font-light italic text-white/60",
          s.approx,
        )}
      >
        N&nbsp;≈
      </span>

      {!positive ? (
        <span className={cn("font-display font-light text-gold", s.big, glow)}>
          0
        </span>
      ) : plainRange ? (
        <span className={cn("font-display font-light text-gold", s.big, glow)}>
          {formatPlain(value)}
        </span>
      ) : (
        <div className={cn("flex items-end justify-center gap-2 text-gold", glow)}>
          <span className={cn("font-mono font-light text-gold/70", s.mantissa)}>
            {mantissa.toFixed(1)} ×
          </span>
          <span className={cn("font-display font-light leading-none", s.base)}>
            10
          </span>
          {/* The exponent is the whole story — make it the loudest thing, and
              pulse it whenever it changes. */}
          <motion.span
            key={exponent}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={cn("font-display font-normal leading-none", s.exp)}
          >
            {toSuperscript(exponent)}
          </motion.span>
        </div>
      )}
    </div>
  );
}

/** Tidy decimal for values comfortably near 1 (no scientific needed). */
function formatPlain(value: number): string {
  if (value >= 100) return Math.round(value).toLocaleString("en-US");
  if (value >= 1) return (Math.round(value * 10) / 10).toString();
  return (Math.round(value * 1000) / 1000).toString();
}
